import {
  type FC,
  useState,
  useEffect,
  useRef,
  forwardRef,
  lazy,
  Suspense,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import classNames from 'classnames/bind';
import { optionalProp } from '../core';
import type { IconName } from '../Icons/Icons';
import { FAIcon, IconStyle } from '../Icons/Icons';
import { TooltipPreference } from '../Tooltip';
import { filterIconTheme } from '../utils/ThemeFilter/ThemeFilter';
import { useDelayedUnmount } from '../hooks';
import styles from './Accordion.module.scss';

import { type AccordionPropsTypes, type AccordionTheme } from './types/propsType';
import { ControlIconType, FontSize, AccordionStyle, BorderDesign, AccordionStatus, AccordionType } from './types/enums';
const Tooltip = lazy(() => import('../Tooltip/Tooltip'));

const cx = classNames.bind(styles);

const getStatusCls = (status: AccordionStatus) => {
  if (status === AccordionStatus.COMPLETE) {
    return IconStyle.SOLID;
  }
  return IconStyle.REGULAR;
};

const computeElement = ({ id, isAccordionOpen }: { id: string; isAccordionOpen: boolean }) => {
  const accordionBody = document.getElementById(`${id}-collapse`);
  if (accordionBody) {
    accordionBody.style.overflow = isAccordionOpen ? 'initial' : 'hidden';
  }
};

const getControlIcon = (isOpen = false, iconType: ControlIconType): IconName => {
  if (iconType === ControlIconType.CHEVRON) {
    return 'chevron-down';
  } else if (isOpen) {
    return 'minus';
  } else {
    return 'plus';
  }
};
/**
 * @description Accordion component .
 * Props ->
 * title: text in accordion (Required),
 * className: prop to get custom styling class,
 * iconType: prop to control icon type,
 * content: text prop to pass content body,
 * children: default component prop to inherit custom HTML template,
 * accordionInfo: flag to control visibility of info tooltip,
 * accordionInfoText: tooltip text to display, defaults to title,
 * contentTitle: title for content body,
 * fontSize: 2 Font Variations Large & Small
 */

const Accordion: FC<AccordionPropsTypes> = forwardRef<HTMLButtonElement, AccordionPropsTypes>(
  (props: AccordionPropsTypes, ref) => {
    const {
      id,
      className = '',
      contentTitle = '',
      content = '',
      title,
      iconType = ControlIconType.CHEVRON,
      borderDesign,
      children,
      accordionInfo = false,
      accordionInfoText,
      onToggleCallback,
      isInCard = true,
      isOpen: isPropAccordionOpen,
      fontSize = FontSize.LARGE,
      isBorderDesign = true,
      labelInfo,
      status = AccordionStatus.INCOMPLETE,
      accordionStyle = AccordionStyle.DEFAULT,
      steppedBody,
      controlIcon,
      accordionType,
      ...restProps
    } = props;

    const [isAccordionOpen, setIsAccordionOpen] = useState(false);
    const collapsibleRef = useRef<HTMLDivElement>(null);
    const timeoutIntervalRef = useRef<NodeJS.Timeout>();
    const handleEvent = (e: ReactKeyboardEvent | ReactMouseEvent) => {
      const isOpen = !isAccordionOpen;
      clearTimeout(timeoutIntervalRef.current);
      setIsAccordionOpen(isOpen);
      onToggleCallback?.(e, isOpen);
    };

    useEffect(() => {
      setIsAccordionOpen(!!isPropAccordionOpen);
    }, [isPropAccordionOpen]);

    useEffect(() => {
      if (isAccordionOpen) {
        timeoutIntervalRef.current = setTimeout(() => {
          computeElement({ id, isAccordionOpen });
        }, 300);
      } else {
        if (timeoutIntervalRef.current) {
          collapsibleRef.current?.classList.add(cx('close-content'));
        }
        clearTimeout(timeoutIntervalRef.current);
        computeElement({ id, isAccordionOpen });
      }
    }, [isAccordionOpen]);

    const { isMounted } = useDelayedUnmount(isAccordionOpen, 300);

    const styleCheck = accordionStyle !== AccordionStyle.STEPPED && (borderDesign || isBorderDesign);
    const CBorderDesignCls = cx({
      [borderDesign || BorderDesign.TOP_BORDER]: styleCheck,
      'card-accordion': !styleCheck && isInCard,
      'button-stepped-open': isAccordionOpen && accordionStyle === AccordionStyle.STEPPED,
    });
    const iconStyle = getStatusCls(status);
    const CClassname = cx(
      'accordion-item',
      className,
      { show: isAccordionOpen },
      { 'compact-accordion': accordionType === AccordionType.COMPACT },
      CBorderDesignCls,
    );
    const CBodyClassname = cx('collapse', { show: isAccordionOpen }, CBorderDesignCls);
    const CControlIconClassname = cx('icon', 'icon-right', controlIcon?.className);
    return (
      <div className={CClassname} id={id} role="region">
        <h2 className={`${styles['accordion-header']}`} id={`${id}-accordion-header`}>
          <button
            id={`${id}-button`}
            className={`${styles['accordion-button']} ${CBorderDesignCls}`}
            data-bs-toggle="collapse"
            data-bs-target={`${id}-collapse`}
            data-testid="accordion-test-id"
            aria-expanded={isAccordionOpen}
            aria-controls={`${id}-collapse`}
            ref={ref}
            onClick={handleEvent}
            {...restProps}
          >
            <span className={styles['accordion-label-container']}>
              <div className={styles['accordion-label-main']}>
                <div className={styles['accordion-label-left']}>
                  {accordionStyle === AccordionStyle.STEPPED && (
                    <FAIcon iconName="circle-check" iconStyle={iconStyle} className={styles[status]} />
                  )}
                  <span className={styles['accordion-label-body']}>
                    <label className={`${styles['accordion-label']} ${styles[fontSize]}`}>{labelInfo || title}</label>
                  </span>
                  {accordionInfo && (
                    <Suspense>
                      <Tooltip
                        message={accordionInfoText || title}
                        preference={TooltipPreference.LEFT}
                        className={styles['icon-info']}
                      />
                    </Suspense>
                  )}
                </div>
                <FAIcon
                  {...optionalProp('theme', filterIconTheme(restProps.theme))}
                  {...optionalProp('iconName', getControlIcon(isAccordionOpen, iconType))}
                  iconStyle={IconStyle.SOLID}
                  {...controlIcon}
                  className={CControlIconClassname}
                  data-testid="accordion-icon-testid"
                />
              </div>
              {accordionStyle === AccordionStyle.STEPPED && steppedBody && (
                <div className={styles['accordion-label-stepped-body']} data-testid="accordion-step-body">
                  {steppedBody}
                </div>
              )}
            </span>
          </button>
        </h2>
        <div
          id={`${id}-collapse`}
          className={CBodyClassname}
          aria-label={`${id}-accordion-header`}
          data-bs-parent={`#${id}`}
          ref={collapsibleRef}
        >
          <div className={`${styles['accordion-body']}`}>
            {content && contentTitle ? (
              <>
                <div className={styles['content-title']}>{contentTitle}</div>
                <div>{content}</div>
              </>
            ) : (
              isMounted && children
            )}
          </div>
        </div>
      </div>
    );
  },
);
Accordion.displayName = 'Accordion';
export default Accordion;
export { ControlIconType, FontSize, AccordionStyle, BorderDesign, AccordionStatus, AccordionType };
export type { AccordionTheme };
